import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { usePatchTaskMutation } from 'API/order/OrderTaskAPI';
import { Formik, Form } from 'formik';
import React, { useMemo, useState, useCallback } from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { IOrderTaskData } from 'types/tasks';
import { Spinner } from 'ui-toolkit';
import { assertDefined } from 'utilities/Assertions';
import { EditTaskSchema } from 'validation/order/TaskSchema';
import { combineDateAndTime, IEditField, taskToEditFormData } from './formTools';

export interface ITaskEditFormDisplayProps {
  task: IOrderTaskData;
  setIsEditing?: () => void;
}

interface ITaskEditFormProps {
  task: IOrderTaskData;
  form: React.FC<IEditField>;
  display: React.FC<ITaskEditFormDisplayProps>;
}

export const TaskEditForm: React.FC<ITaskEditFormProps> = ({ form: FormFields, task, display: Display }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const [isEditing, setIsEditing] = useState(false);

  const [patchTask] = usePatchTaskMutation();

  // Store initial form values to compare against
  const initialFormValues = useMemo(() => taskToEditFormData(task), [task]);

  const handleFormSubmit = useCallback(async (values: any) => {
    const { id, role, teammate, dueDateDate, dueDateTime } = values;

    const newDueDate = combineDateAndTime(dueDateDate, dueDateTime);
    const originalDueDate = combineDateAndTime(initialFormValues.dueDateDate, initialFormValues.dueDateTime);

    const body = {
      id,
      role: role !== initialFormValues.role ? role : undefined,
      teammateId: teammate !== initialFormValues.teammate ? teammate : undefined,
      dueDate: newDueDate !== originalDueDate ? newDueDate : undefined,
    };

    const result = await patchTask({ orderId, body });
    if (checkForMutationSuccess(result, 'updating task', 'Error updating the task.')) {
      setIsEditing(false);
    }
  }, [patchTask, orderId, initialFormValues]);

  return (
    <>
      {!isEditing ? (
        <Display task={task} setIsEditing={() => setIsEditing(true)} />
      ) : (
        <Formik
          enableReinitialize
          validationSchema={EditTaskSchema}
          initialValues={initialFormValues}
          isInitialValid={EditTaskSchema.isValidSync(task)}
          onSubmit={handleFormSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              {isSubmitting ? <Spinner /> : <FormFields submitOnChange cancel={() => setIsEditing(false)} />}
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
